import { RestoreTier } from '@mimir/common/dto/restoreMediaRequestDto';
import type { ColdStorageRestoreOps, ColdStorageRestoreResponse, RestoreStatus } from './coldStorageRestoreOps';
import type { ColdStoragePollOps, RestoredItem } from './coldStoragePollOps';

export interface PostRestoreRequestOfflineResponse {
  objectOnline: false;
  restoreStatus: RestoreStatus;
}

export interface PostRestoreRequestOnlineResponse {
  objectOnline: true;
  highresUrl: string;
}


export type PostRestoreRequestResponse = PostRestoreRequestOfflineResponse | PostRestoreRequestOnlineResponse;

export class ColdStorageRestoreImpl implements ColdStorageRestoreOps {

  constructor(
    /**
     * This function is responsible for invoking the restoreMedia lambda and normalizing
     * its response.
     */
    private postRestoreRequest: (itemId: string, restoreTier: RestoreTier, retentionDays: number) => Promise<PostRestoreRequestResponse>,

    /**
     * This function will be invoked with the response received from postRestoreRequest().
     * It is meant to perform things like UI updates.
     */
    private processResponse: (itemId: string, requestedTier: RestoreTier, response: PostRestoreRequestResponse) => Promise<void>,

    /**
     * This function will be invoked once the restore operation is complete.
     */
    private processRestoredItem: (item: RestoredItem) => Promise<void>,

    /**
     * These are the objects to use for slow and fast polling.
     */
    private expeditedPollOps: ColdStoragePollOps,
    private slowPollOps: ColdStoragePollOps,
  ) {}


  async requestRestore(itemId: string, restoreTier: RestoreTier, retentionDays: number): Promise<ColdStorageRestoreResponse> {
    const postRestoreRequestResponse = await this.postRestoreRequest(itemId, restoreTier, retentionDays);
    await this.processResponse(itemId, restoreTier, postRestoreRequestResponse);

    if (postRestoreRequestResponse.objectOnline) {
      return {
        objectOnline: true,
        highresUrl: postRestoreRequestResponse.highresUrl,
      };
    }

    // If the user has asked for Expedited, let's use fast polling. The worst case scenario
    // is that AWS actually refuses and that we issue a Standard request instead, in which case
    // we will poll anyway like if we were dealing with an Expedited restore.
    const useFastPolling = (restoreTier === RestoreTier.Expedited);

    const restoreCompletionPromise = (useFastPolling ? this.expeditedPollOps : this.slowPollOps).waitForRestore(itemId);

    void restoreCompletionPromise.then(restoreItem => this.processRestoredItem(restoreItem));
    return {
      objectOnline: false,
      restoreStatus: postRestoreRequestResponse.restoreStatus,
      restoreCompletionPromise,
    };
  }

}
